<template>
    <div class="md:container">
        <portal to="header">
            <ProfileShowHeader></ProfileShowHeader>
        </portal>

        <div class="flex flex-col md:flex-row mx-auto">
            <div class="w-full md:w-1/2 mb-4 md:mb-0 md:pr-4">
                <Panel
                    class="w-full md:h-full py-6 max-md:container md:p-6"
                    :title="titleStudiesPanel"
                >
                    <ProfileShowStudy
                        v-if="userStudies.length > 0"
                        :study="selectedStudy"
                    ></ProfileShowStudy>
                    <Spinner
                        v-else-if="isLoading"
                        appearance="circular"
                        color="none"
                    />
                    <div v-else>
                        <span v-text="$t('profile:show.studies.no_studies_error')"></span>
                    </div>
                    <template #header-options>
                        <Btn
                            v-if="user?.is_pupil"
                            data-testid="profile-show-reset-studies"
                            :label="$t('profile:show.studies.btn_reset_studies')"
                            icon="edit"
                            color="primary"
                            variant="tonal"
                            @click="onResetStudies"
                        ></Btn>
                        <Select
                            v-if="hasMoreThanOneStudy"
                            v-model="selectedStudyIndex"
                            class="study-selector"
                            appearance="menu"
                            value-field="value"
                            rounded="md"
                            :options="studyIndexOptions"
                        ></Select>
                    </template>
                </Panel>
            </div>
            <div class="w-full md:w-1/2 mb-4 md:mb-0 md:pl-4">
                <ProfileShowStats></ProfileShowStats>
            </div>
        </div>
        <div
            class="activity-content mb-10"
            style="min-height: 400px"
        >
            <div
                v-if="isOwner"
                class="md:mt-6"
            >
                <Btn
                    data-testid="profile-my-documents-btn"
                    class="px-4 py-2 block bg-white btn-link"
                    right-icon="chevron-right"
                    content-spacing="apart"
                    variant="custom"
                    left-icon="documents"
                    compact
                    :href="
                        route('profile.study-material.list', {
                            type: 'documents',
                        })
                    "
                >
                    <div
                        class="pl-2 md:pl-4 !font-normal"
                        v-text="$t('profile:show.links.documents')"
                    ></div
                ></Btn>
                <Btn
                    data-testid="profile-my-flashcards-btn"
                    class="px-4 py-2 block bg-white btn-link"
                    right-icon="chevron-right"
                    content-spacing="apart"
                    variant="custom"
                    compact
                    left-icon="flashcards"
                    :href="
                        route('profile.study-material.list', {
                            type: 'flashcards',
                        })
                    "
                >
                    <div
                        class="pl-2 md:pl-4 !font-normal"
                        v-text="$t('profile:show.links.flashcards')"
                    ></div
                ></Btn>
                <Btn
                    v-if="!user.is_pupil"
                    class="btn-link border-2 bg-orange-50 border-solid border-orange-100 justify-between items-center px-4 py-2 text-blue-600 flex text-base rounded-lg hover:bg-orange-100 active:bg-orange-200"
                    :href="route('career.corner.index')"
                    target="_blank"
                    left-icon="job"
                    content-spacing="apart"
                    variant="custom"
                    compact
                    data-click="Profile,Job search"
                    @click="storeJobSeekerStatus()"
                >
                    <template #right><i class="icon md:text-primary icon-chevron-right"></i></template>
                    <div class="flex flex-row w-full items-center justify-between text-left text-black">
                        <div class="flex flex-row">
                            <div
                                class="hidden md:block pl-2 md:pl-4 font-medium"
                                v-text="$t('profile:show.links.jobs')"
                            ></div>
                            <div
                                class="block md:hidden pl-2 md:pl-4 font-medium"
                                v-text="$t('profile:show.links.jobs_short')"
                            ></div>
                        </div>
                        <span
                            class="hidden md:block mr-2 text-blue-600 font-medium"
                            v-text="$t('profile:show.links.jobs_link')"
                        ></span>
                    </div>
                </Btn>
                <Btn
                    v-if="!user.is_pupil"
                    class="btn-link cursor-pointer border-2 bg-orange-50 border-solid border-orange-100 justify-between items-center !px-4 !py-2.5 text-blue-600 flex text-base !rounded-lg hover:bg-orange-100 active:bg-orange-200"
                    left-icon="company"
                    :href="`${route('career.corner.index')}#ShowCompanies`"
                    variant="custom"
                    compact
                    data-click="Profile,Company search"
                    content-spacing="apart"
                    @click="storeJobSeekerStatus()"
                >
                    <template #right><i class="icon text-primary icon-chevron-right"></i></template>
                    <div class="flex flex-row w-full items-center justify-between text-left text-black">
                        <div class="flex flex-row">
                            <div
                                class="hidden md:block pl-2 md:pl-4 font-medium"
                                v-text="$t('profile:show.links.companies')"
                            ></div>
                            <div
                                class="block md:hidden pl-2 md:pl-4 font-medium"
                                v-text="$t('profile:show.links.companies_short')"
                            ></div>
                        </div>
                        <span
                            class="hidden md:block mr-2 text-blue-600 font-medium"
                            v-text="$t('profile:show.links.companies_link')"
                        ></span>
                    </div>
                </Btn>
            </div>
            <div
                v-else
                class="mt-4 md:mt-8"
            >
                <TabsPanel
                    model-value="documents"
                    panel-list-class="mb-4 md:mb-6"
                    class="mt-6 md:mt-3"
                >
                    <TabsPanelTab
                        id="documents"
                        :name="documentTabLabel"
                    >
                        <ProfileShowDocuments :user-id="profile.id" />
                    </TabsPanelTab>
                </TabsPanel>
            </div>
        </div>
        <TheFooterStatic></TheFooterStatic>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Panel from '@components/Panel';
import TabsPanel from '@components/TabsPanel';
import TabsPanelTab from '@components/TabsPanelTab';
import TheFooterStatic from '@components/TheFooterStatic';
import JobSeekerMixin from '@/mixins/jobSeekerMixin';
import route from '@router/route';
import { removeUserStudies } from '@api/gateway/legacy-api/study';
import { initializeClickListeningToTrack, trackEvent } from '@/modules/tracking';
import ProfileShowHeader from './ProfileShowHeader.vue';
import ProfileShowStudy from './ProfileShowStudy.vue';
import ProfileShowStats from './ProfileShowStats.vue';
import ProfileShowDocuments from './ProfileShowDocuments.vue';

export default {
    name: 'ProfileShow',
    components: {
        TheFooterStatic,
        Panel,
        TabsPanel,
        TabsPanelTab,
        ProfileShowHeader,
        ProfileShowStats,
        ProfileShowStudy,
        ProfileShowDocuments,
    },
    extends: JobSeekerMixin,
    i18nOptions: {
        namespaces: ['common', 'profile'],
    },
    data() {
        return {
            profile: window.sdWindow.profile,
            selectedStudyIndex: 0,
            isLoading: true,
            removeClickListener: null,
        };
    },
    computed: {
        ...mapGetters('ui', ['getLocale']),
        ...mapGetters({
            user: 'auth/user',
            isLoggedIn: 'auth/isLoggedIn',
            currentProfile: 'profile/profile',
            isProfileOwner: 'profile/isProfileOwner',
            documentsCount: 'profile/documentsTotalCount',
        }),
        ...mapGetters('study', ['userStudies']),
        selectedStudy() {
            if (this.userStudies.length === 0) {
                return null;
            }
            return this.userStudies[this.selectedStudyIndex];
        },
        hasMoreThanOneStudy() {
            return this.userStudies && this.userStudies.length > 1;
        },
        titleStudiesPanel() {
            return this.isOwner ? this.$t('profile:show.studies.title_owner') : this.$t('profile:show.studies.title');
        },
        documentTabLabel() {
            return (
                this.$t('profile:show.guest.shared_documents') +
                (this.documentsCount !== null ? ` (${this.documentsCount})` : ``)
            );
        },
        lang() {
            return this.getLocale.slice(0, 2);
        },
        isOwner() {
            return this.isProfileOwner(this.user);
        },
        studyIndexOptions() {
            return [0, 1, 2].splice(0, this.userStudies.length).map((i) => ({
                value: i,
                label: this.$t(`profile:show.studies.type.${i + 1}`),
            }));
        },
    },
    created() {
        this.$store.dispatch('study/initialize', this.profile.id).then(() => {
            this.isLoading = false;
        });
        this.$store.dispatch('profile/setProfile', this.profile);
    },
    mounted() {
        this.removeClickListener = initializeClickListeningToTrack(this.$el, 'Career');
    },
    beforeUnmount() {
        this.removeClickListener();
    },
    methods: {
        route,
        async onResetStudies() {
            trackEvent('user_profile', 'profile_studies_reset_button_clicked');

            const confirmed = await this.$modal.confirmDanger({
                title: this.$t('profile:show.studies.modal_reset_studies_title'),
                text: this.$t('profile:show.studies.modal_reset_studies_text'),
            });

            if (!confirmed) return;

            removeUserStudies(this.user.userid)
                .then(() => window.location.reload())
                .catch((error) => {
                    this.$store.dispatch('flashMessages/showError', {
                        content: error.response?.data?.message || this.$t('errors.requestError'),
                    });
                });
        },
    },
};
</script>
<style lang="scss" scoped>
.btn-link {
    @apply text-base text-black w-full no-underline mb-4 border-0 shadow rounded-none;
    @screen md {
        @apply rounded-lg;
    }
    &:active,
    &:hover {
        @apply no-underline;
    }
}
.study-selector {
    min-width: 8rem;
}
</style>
